import { useEffect, useMemo, useRef, useState } from 'react';
import { AccommodationListingDTO } from '../AccommodationsConsole.types';
import equals from 'deep-equal';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Filters } from '../AccommodationsConsole.types';

const PAGE_SIZE = 30;

type UsePaginationArgs = {
  listings: AccommodationListingDTO[];
  filters: Filters;
};

export const usePagination = (args: UsePaginationArgs) => {
  const { listings, filters } = args;

  const listingsContainerRef = useRef<HTMLDivElement>(null);
  const filteredPropertyIdsRef = useRef(listings.map(x => x.id));

  const [page, setPage] = useState(0);

  useEffect(() => {
    const nextPropertyIds = listings.map(x => x.id);

    if (!equals(filteredPropertyIdsRef.current, nextPropertyIds)) {
      filteredPropertyIdsRef.current = nextPropertyIds;

      // If the listings have changed, reset the page to 0 + scroll to top
      if (listingsContainerRef.current) {
        listingsContainerRef.current.scrollTo({
          top: 0,
          behavior: 'instant',
        });
      }
      setPage(0);
    }
  }, [listings, filters.pickedByCouple]);

  const { hasMoreListingsToShow, paginatedListings } = useMemo(() => {
    const numberOfListingsToShow = (page + 1) * PAGE_SIZE;
    return {
      hasMoreListingsToShow: numberOfListingsToShow < listings.length,
      paginatedListings: listings.slice(0, numberOfListingsToShow),
    };
  }, [page, listings]);

  const showNextPageOfListings = useEventCallback(() => {
    setPage(prevPage => prevPage + 1);
  });

  return {
    paginatedListings,
    hasMoreListingsToShow,
    showNextPageOfListings,
    listingsContainerRef,
  };
};
